import React, { FC, useCallback, useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Author, Editor, FigureVersion, Publication } from '@graphql/types';
import { ImageBlotProps } from '@components/TextEditor/Blots/ImageBlot/ImageBlotProps';
import RelatedPublications from '@components/UI/RelatedPublicationsAuthor/RelatedPublicationsAuthor';
import AuthorBlockModal from '@components/Authors/AuthorsEditor/AuthorBlockModal/AuthorBlockModal';
import { useStore } from '@stores/rootStoreContext';
import { redColor } from '@theme/vars';
import { useEditorsQuery } from '@components/Authors/AuthorsList/gql/authors.generated';
import { useUpdateAuthorMutation, usePublicationsByAuthorUidQuery } from './gql/AuthorEditor.generated';
import {AUTHOR_FORM_CONSTS} from './AuthorForm.consts'
import {
  AuthorFormWrapper, 
  HeaderWithBorder, 
  HeaderContent, 
  MainContainer, 
  ImageContainer, 
  InfoContainer,
  Row,
  Field,
  StyledInput,
  StyledDescriptionInput,
  StyledSelect,
  StyledCheckboxContainer,
  StyledSaveButton
} from './AuthorForm.styles'


type AuthorFormProps = {
  author: Author;
};

type AuthorEditorFormProps = {
  fullName: string;
  jobTitle: string;
  description: string;
  isActive: boolean;
  status: boolean;
  mail: string;
  editorId?: string | null;
  uid: string;
  publications: Publication[];
  figureVersion?: FigureVersion | null;
};

const AuthorEditor: FC<AuthorFormProps> = ({ author }) => {
  const history = useHistory();
  const [updateAuthorMutation] = useUpdateAuthorMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { cropperStore } = useStore();
  const { cropper, setCropper } = cropperStore;
  const [publicationsServer, setPublicationsServer] = useState<Publication[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [figureVersion, setFigureVersion] = useState<FigureVersion>(author?.figureVersion || {} as FigureVersion);
  const [invalidMail, setInvalidMail] = useState('');
  const [invalidFullName, setInvalidFullName] = useState('');
  const [disabledStatus, setDisabledStatus] = useState('');
  const [blockImage, setBlockImage] = useState<ImageBlotProps>({
    id: 'author-edit',
    src: author?.figureVersion?.primaryProcessedImage?.url || '',
    cropperPreviewUrl: author?.figureVersion?.figure?.cropperPreview?.url || '',
    crop: {
      width: author?.figureVersion?.crop?.width || 0,
      height: author?.figureVersion?.crop?.height || 0,
      top: author?.figureVersion?.crop?.top || 0,
      left: author?.figureVersion?.crop?.left || 0,
    },
    figureId: author?.figureVersion?.figure?.id || '',
    figureUid: author?.figureVersion?.figure?.uid || '',
    figureVersionId: author?.figureVersion?.id || '',
    isFullWidth: true,
    isannouncingphoto: false,
    isoverviewblock: false,
    hasWatermark: false,
    hasDiagonalWatermark: false,
  });

    const { data: editorsData } = useEditorsQuery();

    const { data, fetchMore, refetch } = usePublicationsByAuthorUidQuery({
        variables: {
            authorUid: author.uid,
            first: 10,
            after: author?.publications?.pageInfo?.endCursor || null
        },
        fetchPolicy: 'cache-and-network',
        skip: !author?.uid || author?.publications?.pageInfo?.endCursor === null
    });

  const defaultValues: AuthorEditorFormProps = {
    fullName: [author?.lastName, author?.firstName, author?.surname].filter(Boolean).join(' ') || '',
    jobTitle: author?.jobTitle || '',
    description: author?.description || '',
    isActive: author?.isActive || false,
    status: author?.status || false,
    mail: author?.mail || '',
    editorId: author?.editor?.id || null,
    uid: author?.uid || '',
    publications: author?.publications?.edges
      ?.map(edge => edge?.node as Publication)
      .filter(Boolean) as Publication[],
    figureVersion: author?.figureVersion || null,
  };

  const { control, handleSubmit, formState, setValue, watch, register, ...methodsForm } = useForm<AuthorEditorFormProps>({
    defaultValues,
  });

  const { fullName, mail, jobTitle, description, editorId } = watch();

    useEffect(() => {
        if (author?.uid && author?.publications?.pageInfo?.endCursor !== null) {
            setPublicationsServer([]);
            refetch();
        }
    }, [author, refetch]);

    useEffect(() => {
        if (data?.publications?.edges) {
            const newPublications = data.publications.edges.map(edge => edge?.node as Publication).filter(Boolean);
            setPublicationsServer(newPublications);
        }
    }, [data]);

  useEffect(() => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setInvalidMail(mail && !regex.test(mail) ? AUTHOR_FORM_CONSTS.invalidMail : '');
  }, [mail]);

  useEffect(() => {
    const isFormEmpty =
      !fullName?.trim() &&
      !jobTitle?.trim() &&
      !description?.trim() &&
      !mail?.trim() &&
      !editorId;

    const isRequiredFieldsMissing = !fullName || !editorId;

    const hasErrors = !!invalidMail;

    setDisabledStatus(hasErrors || isFormEmpty || isRequiredFieldsMissing);
  }, [fullName, jobTitle, description, mail, editorId, invalidFullName, invalidMail]);

  useEffect(() => {
    const initialPublications = author?.publications?.edges
      ?.map(edge => edge?.node as Publication)
      .filter(Boolean) as Publication[];
    const allPublications = [...initialPublications, ...publicationsServer];
    setValue('publications', allPublications);
  }, [author?.publications, publicationsServer, setValue]);

  const setNewFigureVersion = useCallback(
    (value: FigureVersion | undefined) => {
      if (value) {
        const newFigureVersion = {
          crop: {
            width: cropper.width || value.crop?.width || 0,
            height: cropper.height || value.crop?.height || 0,
            top: cropper.top || value.crop?.top || 0,
            left: cropper.left || value.crop?.left || 0,
          },
          figure: value.figure,
          hasDiagonalWatermark: false,
          hasWatermark: false,
          primaryProcessedImage: value.primaryProcessedImage,
          uid: value.uid,
          id: value.id,
        } as FigureVersion;

        setFigureVersion(newFigureVersion);
        setValue('figureVersion', newFigureVersion);
        setBlockImage({
          id: 'author-edit' + (value.figure?.id || ''),
          src:
            value.figure?.latestVersion?.primaryProcessedImage?.url ||
            value.primaryProcessedImage?.url ||
            '',
          cropperPreviewUrl: value.figure?.cropperPreview?.url || '',
          crop: {
            width: cropper.width || value.crop?.width || 0,
            height: cropper.height || value.crop?.height || 0,
            top: cropper.top || value.crop?.top || 0,
            left: cropper.left || value.crop?.left || 0,
          },
          figureId: value.figure?.id || '',
          figureUid: value.figure?.uid || '',
          figureVersionId: value.id || '',
          isFullWidth: true,
          isannouncingphoto: false,
          isoverviewblock: false,
          hasWatermark: false,
          hasDiagonalWatermark: false,
        });
        setCropper({
          width: value.crop?.width || 0,
          height: value.crop?.height || 0,
          top: value.crop?.top || 0,
          left: value.crop?.left || 0,
        });
      } else {
        setFigureVersion(null);
        setValue('figureVersion', null);
        setBlockImage({
          id: 'author-edit',
          src: '',
          cropperPreviewUrl: '',
          crop: { width: 0, height: 0, top: 0, left: 0 },
          figureId: '',
          figureUid: '',
          figureVersionId: '',
          isFullWidth: true,
          isannouncingphoto: false,
          isoverviewblock: false,
          hasWatermark: false,
          hasDiagonalWatermark: false,
        });
      }
    },
    [cropper, setValue, setCropper]
  );

  const openModalImage = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const onBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const onSubmitAuthor = useCallback(
    async (data: AuthorEditorFormProps) => {
      try {
        const cleanedFullName = data.fullName.trim();
        await updateAuthorMutation({
          variables: {
            data: {
              authorUid: data.uid,
              fullName: cleanedFullName,
              jobTitle: data.jobTitle,
              description: data.description,
              isActive: data.isActive,
              status: data.status,
              mail: data.mail,
              editor: data.editorId || null,
              figureVersionId: data.figureVersion?.id || null,
            },
          },
        });
        enqueueSnackbar('Автор сохранен', { variant: 'success' });
      } catch (e) {
        enqueueSnackbar('Ошибка при сохранении автора', { variant: 'error' });
      }
    },
    [enqueueSnackbar, updateAuthorMutation]
  );

    useEffect(() => {
        if (data?.publications?.edges) {
            setPublicationsServer(prev => {
                const newPublications = data.publications.edges
                    .map(edge => edge?.node as Publication)
                    .filter(Boolean);

                const combined = [...prev, ...newPublications].reduce((acc, current) => {
                    if (!acc.some(pub => pub.uid === current.uid)) {
                        acc.push(current);
                    }
                    return acc;
                }, [] as Publication[]);

                return combined;
            });
        }
    }, [data?.publications?.edges]);

    const fetchMoreData = useCallback(() => {
        if (!data?.publications?.pageInfo?.endCursor || !data?.publications?.pageInfo?.hasNextPage) {
            return;
        }

        void fetchMore({
            variables: {
                first: 10,
                after: data.publications.pageInfo.endCursor,
            },
        });
    }, [data?.publications?.pageInfo, fetchMore]);

    const allPublications = useMemo(() => {
        const initial = author?.publications?.edges
            ?.map(edge => edge?.node as Publication)
            .filter(Boolean) || [];

        return [...initial, ...publicationsServer];
    }, [author?.publications?.edges, publicationsServer]);

  return (
    <FormProvider {...{ control, handleSubmit, formState, setValue, watch, register, ...methodsForm }}>
      <AuthorFormWrapper>
        <form noValidate onSubmit={handleSubmit(onSubmitAuthor)}>
          <HeaderWithBorder>
            <IconButton onClick={onBack}>
              <ArrowBackIcon />
            </IconButton>
            <HeaderContent>
              <Typography variant="h1">
                {author?.firstName === '' ? AUTHOR_FORM_CONSTS.createAuthor : AUTHOR_FORM_CONSTS.editAuthor}
              </Typography>
            </HeaderContent>
          </HeaderWithBorder>
          <MainContainer>
            <ImageContainer id="author-edit-image" onClick={openModalImage}>
              {blockImage && (
                <figure {...blockImage}>
                  {blockImage.src && (
                    <img
                      className="change-item"
                      draggable="false"
                      src="/changeItem.jpg"
                      onClick={openModalImage}
                    />
                  )}
                  <img src={blockImage.src || '/noPhoto.jpg'} draggable="false" />
                </figure>
              )}
            </ImageContainer>
            <InfoContainer>
              <Row>
                <Field>
                  <InputLabel>{AUTHOR_FORM_CONSTS.fullName.label}</InputLabel>
                  <StyledInput
                    {...register('fullName')}
                    fullWidth
                    placeholder={AUTHOR_FORM_CONSTS.fullName.placeholder}
                    error={!!invalidFullName}
                  />
                  {invalidFullName && (
                    <FormHelperText style={{ color: redColor }}>{invalidFullName}</FormHelperText>
                  )}
                </Field>
                <Field>
                  <InputLabel>{AUTHOR_FORM_CONSTS.jobTitle.label}</InputLabel>
                  <StyledInput
                    {...register('jobTitle')}
                    fullWidth
                    placeholder={AUTHOR_FORM_CONSTS.jobTitle.placeholder}
                  />
                </Field>
              </Row>
              <Row>
                <Field>
                  <InputLabel>{AUTHOR_FORM_CONSTS.email.label}</InputLabel>
                  <StyledInput
                    {...register('mail')}
                    fullWidth
                    placeholder={AUTHOR_FORM_CONSTS.email.placeholder}
                    error={!!invalidMail}
                    endAdornment={
                      <InputAdornment position="end">
                        {mail && (
                          <IconButton edge="end" size="small" onClick={() => setValue('mail', '')}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                      </InputAdornment>
                    }
                  />
                  {invalidMail && (
                    <FormHelperText style={{ color: redColor }}>{invalidMail}</FormHelperText>
                  )}
                </Field>
                <Field>
                  <InputLabel>{AUTHOR_FORM_CONSTS.editor.label}</InputLabel>
                  <FormControl fullWidth>
                    <StyledSelect {...register('editorId')} value={watch('editorId') || '' }>
                      <MenuItem value="">{AUTHOR_FORM_CONSTS.editor.defaultValue}</MenuItem>
                      {editorsData?.editors?.map((editor: Editor) => (
                        <MenuItem key={editor.uid} value={editor.uid}>
                          {editor.name}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </FormControl>
                </Field>
              </Row>
              <Box>
                <InputLabel>{AUTHOR_FORM_CONSTS.description.label}</InputLabel>
                <StyledDescriptionInput
                  {...register('description')}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder={AUTHOR_FORM_CONSTS.description.placeholder}
                />
              </Box>
                          <StyledCheckboxContainer>
                              <Checkbox
                                  {...register('isActive')}
                                  checked={watch('isActive')}
                                  onChange={(e) => setValue('isActive', e.target.checked)}
                              />
                              <span>{AUTHOR_FORM_CONSTS.isActive}</span>

                              <Checkbox
                                  {...register('status')}
                                  checked={watch('status')}
                                  onChange={(e) => setValue('status', e.target.checked)}
                              />
                              <span>{AUTHOR_FORM_CONSTS.status}</span>
                          </StyledCheckboxContainer>
              <StyledSaveButton type="submit" disabled={disabledStatus}>
                {AUTHOR_FORM_CONSTS.saveText}
              </StyledSaveButton>
            </InfoContainer>
          </MainContainer>
          <Divider />
          <Box width="100%">
            <Box>
              {allPublications.length ? (
                              <InfiniteScroll
                                  dataLength={allPublications.length}
                                  next={fetchMoreData}
                                  hasMore={!!data?.publications?.pageInfo?.hasNextPage}
                                  loader={<LinearProgress color="primary" />}
                                  height="35vh"
                              >
                                  <RelatedPublications publications={allPublications} />
                              </InfiniteScroll>
              ) : (
                <Box marginTop="15px">
                  <Typography variant="h2">{AUTHOR_FORM_CONSTS.publicationNone}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        <AuthorBlockModal
            figureVersion={figureVersion}
            open={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            setFigureVersion={setNewFigureVersion}
          />
        </form>
      </AuthorFormWrapper>
    </FormProvider>
  );
};

export default AuthorEditor;